// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-launch/ExecutiveMissionBrief.jsx

import React, { useEffect, useRef, useState } from 'react';
import MissionCard from './shared/MissionCard';
import MissionMetric from './shared/MissionMetric';
import ExecutiveSummaryCard from './shared/ExecutiveSummaryCard';

function truthLabel(metric = {}) {
  const classification = String(metric.classification || '').toLowerCase();
  if (classification === 'measured') return 'Measured';
  if (classification === 'derived') return 'Derived';
  if (classification === 'modeled') return 'Modeled launch estimate';
  if (classification === 'unavailable') return 'Unavailable';
  return 'Source not classified';
}

function truthValue(metric = {}, fallback = '—') {
  if (metric.available === false || metric.value === null || metric.value === undefined) {
    return fallback;
  }
  return metric.value;
}

function truthDetail(metric = {}, context = '') {
  if (metric.available === false) {
    return metric.reason
      ? `Unavailable · ${metric.reason}`
      : 'Unavailable';
  }

  const label = truthLabel(metric);
  return context ? `${label} · ${context}` : label;
}


const SERVICE_ORDER = ['s3', 'iam', 'cloudtrail', 'aws_config', 'ec2'];
const SERVICE_NAMES = { s3: 'Amazon S3', iam: 'AWS IAM', cloudtrail: 'AWS CloudTrail', aws_config: 'AWS Config', ec2: 'Amazon EC2' };
const SERVICE_GUIDANCE = {
  s3: { significance: 'A bounded storage inventory does not establish coverage across all accounts or regions.', action: 'Confirm the intended storage scope and evaluate recorded S3 configuration decisions.', limitation: 'Zero observed buckets is not proof of no S3 use or of compliance.' },
  iam: { significance: 'Observed IAM evidence does not prove identity access controls are effective.', action: 'Review the recorded identity configurations against the applicable access control objectives.', limitation: 'Specific IAM weaknesses must be supported by detailed findings, not inferred from receipt verification.' },
  cloudtrail: { significance: 'An observed collection result does not establish organization-wide audit logging effectiveness.', action: 'Review trail coverage, retention, and configuration against applicable objectives.', limitation: 'Collection verification alone does not prove log completeness or effective monitoring.' },
  aws_config: { significance: 'Unavailable configuration visibility limits assessment of configuration recording.', action: 'Ask an authorized AWS owner to investigate Config recording and the reason for unavailable coverage.', limitation: 'Unavailable observation is not a verified compliance failure.' },
  ec2: { significance: 'Partial observation does not represent all possible compute resources.', action: 'Confirm applicable AWS accounts and regions, then assess whether additional authorized observation is required.', limitation: 'Unobserved resources cannot be classified as compliant or noncompliant.' },
};
function humanCoverage(value) { return String(value ?? 'Unknown').replaceAll('_', ' ').replace(/\b\w/g, c => c.toUpperCase()); }
function serviceAssessment(key, record, scanEvidence) {
  const coverage = key === 's3' ? 'observed' : record.coverage;
  const guidance = SERVICE_GUIDANCE[key];
  const observation = key === 's3'
    ? `${scanEvidence.observed_bucket_count ?? 'Unknown'} buckets observed within the bounded S3 inventory.`
    : `${SERVICE_NAMES[key]} evidence verified; observation coverage: ${humanCoverage(coverage).toLowerCase()}.`;
  const interpretation = key === 's3' ? null : record?.interpretation;
  const grounded = interpretation && typeof interpretation === 'object' ? {
    significance: interpretation.operational_implication || guidance.significance,
    action: interpretation.next_investigation || guidance.action,
    limitation: interpretation.scope_boundary || guidance.limitation,
  } : guidance;
  return { key, title: SERVICE_NAMES[key], coverage, observation, ...grounded,
    codes: key === 's3' ? [] : record.limitation_codes || [] };
}

export default function ExecutiveMissionBrief({
  brief = {},
  loading = false,
  error = '',
  saving = false,
  onRetry,
  onContinue,
  readOnly = false,
}) {
  const [selectedService, setSelectedService] = useState(null);
  const [reviewAcknowledged, setReviewAcknowledged] = useState(false);
  const closeRef = useRef(null);
  const dialogRef = useRef(null);
  const triggerRef = useRef(null);
  const unified = brief.unified_aws_mission_brief;
  const records = Array.isArray(unified?.services) ? unified.services : [];
  const recordsByService = Object.fromEntries(records.map(record => [record.service, record]));
  const fiveServiceReady = brief.evidence_collection_verified === true &&
    brief.scan_evidence?.available === true && unified?.state === 'available' &&
    unified?.source === 'verified_historical_attempt_receipts' &&
    unified?.assurance_evaluated === false && unified?.verified_service_count === 4 &&
    records.length === 4 && SERVICE_ORDER.slice(1).every(key => recordsByService[key]?.evidence_verified === true) &&
    String(unified?.attempt_id) === String(brief.scan_evidence?.attempt_id);
  const serviceEntries = fiveServiceReady ? SERVICE_ORDER.map(key => serviceAssessment(key, recordsByService[key], brief.scan_evidence)) : [];
  const activeService = serviceEntries.find(item => item.key === selectedService);
  function openService(key) { triggerRef.current = document.activeElement; setSelectedService(key); }
  function closeService() { setSelectedService(null); }
  useEffect(() => {
    if (!selectedService || !fiveServiceReady) return undefined;
    closeRef.current?.focus();
    function onKey(event) {
      if (event.key === 'Escape') { event.preventDefault(); closeService(); }
      if (event.key === 'Tab') {
        const nodes = Array.from(dialogRef.current?.querySelectorAll('button:not(:disabled), [href], input:not(:disabled), [tabindex]:not([tabindex="-1"])') || []);
        if (!nodes.length) { event.preventDefault(); dialogRef.current?.focus(); return; }
        if (event.shiftKey && document.activeElement === nodes[0]) { event.preventDefault(); nodes[nodes.length - 1].focus(); }
        else if (!event.shiftKey && document.activeElement === nodes[nodes.length - 1]) { event.preventDefault(); nodes[0].focus(); }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); triggerRef.current?.focus(); };
  }, [selectedService, fiveServiceReady]);

  const truth = brief.metric_truth || {};

  const executiveMetrics = [
    {
      label: 'Current Posture',
      value: truthValue(truth.current_posture),
      detail: truthDetail(
        truth.current_posture,
        'Review Mission Control for measured posture'
      ),
    },
    {
      label: 'Critical Findings',
      value: truthValue(truth.critical_findings),
      detail: truthDetail(
        truth.critical_findings,
        'Review normalized findings'
      ),
      signal: 'warning',
    },
    {
      label: 'Quick Wins',
      value: truthValue(truth.quick_wins),
      detail: truthDetail(
        truth.quick_wins,
        'Confirm persisted work in Mission Execution'
      ),
    },
    {
      label: 'Operational Readiness',
      value: truthValue(truth.operational_readiness),
      detail: truthDetail(
        truth.operational_readiness,
        'Onboarding readiness only'
      ),
    },
  ];

  const executionPrepMetrics = [
    {
      label: 'Connector Health',
      value: truthValue(truth.connector_health),
      detail: truthDetail(
        truth.connector_health,
        'Selected provider state'
      ),
    },
    {
      label: 'Recommendations Ready',
      value: truthValue(truth.recommendations_ready),
      detail: truthDetail(
        truth.recommendations_ready,
        'Confirm persisted recommendations'
      ),
    },
    {
      label: 'Estimated Remediation',
      value: truthValue(truth.estimated_remediation_hours),
      detail: truthDetail(truth.estimated_remediation_hours),
    },
    {
      label: 'Learning Engine',
      value: truthValue(truth.learning_engine, 'Advisory'),
      detail: truthDetail(
        truth.learning_engine,
        'Human approval required'
      ),
    },
  ];

  return (
    <div className="mission-step-shell mission-executive-brief-page">
      <MissionCard
        eyebrow="Phase 5 · Executive Mission Brief"
        title={
          brief.heading ||
          brief.title ||
          'Your first compliance operating picture is ready'
        }
        description={
          brief.description ||
          'OASYS summarizes the launch picture while clearly separating measured state, derived indicators, modeled estimates, and unavailable values.'
        }
      >
        {error ? (
          <div className="connector-launch-error" role="alert">
            <strong>Executive Mission Brief unavailable.</strong>
            <span>{error}</span>
            {typeof onRetry === 'function' ? (
              <button type="button" onClick={onRetry}>Retry</button>
            ) : null}
          </div>
        ) : null}

        {!brief.scan_evidence?.available ? (
        <ExecutiveSummaryCard
          brief={{
            title:
              brief.status_title ||
              brief.title ||
              'Executive Mission Brief',
            summary:
              brief.summary ||
              'Your organization has completed the first launch scan. Mission Control remains the authoritative source for measured operational posture.',
            metrics: executiveMetrics,
          }}
        />
        ) : null}

        <section aria-label="Evidence-grounded Mission Brief" className="oasys-five-brief">
          {fiveServiceReady ? (
            <>
              <div className="oasys-five-brief-summary">
                <p className="mission-launch-eyebrow">VERIFIED AWS OBSERVATIONS</p>
                <h3>Executive AWS assessment</h3>
                <p>{brief.summary || 'Five AWS service receipts were verified for the same completed Mission Scan attempt. Coverage varies by service.'}</p>
                <p className="oasys-five-brief-boundary">Evidence verification does not establish SOC 2 assurance, control effectiveness, or absence of risk.</p>
              </div>
              <div className="oasys-five-brief-metrics" aria-label="Evidence summary">
                <div><strong>5 / 5</strong><span>Evidence receipts verified</span><small>Coverage varies by service</small></div>
                <div><strong>{serviceEntries.filter(item => item.coverage !== 'observed').length}</strong><span>Coverage limitations</span></div>
                <div><strong>Not evaluated</strong><span>Compliance assurance</span></div>
              </div>
              <h3>Service assessments</h3>
              <div className="oasys-five-brief-cards">
                {serviceEntries.map(item => (
                  <button key={item.key} type="button" className="oasys-five-brief-card oasys-brief-presentation-r1" data-coverage={item.coverage} onClick={() => openService(item.key)} aria-haspopup="dialog" aria-label={`View ${item.title} assessment`}>
                    <span className="oasys-five-brief-card-top"><strong>{item.title}</strong><span className="oasys-brief-coverage-pill">{humanCoverage(item.coverage)}</span></span>
                    <span className="oasys-brief-card-caption">Verified receipt · {humanCoverage(item.coverage)} coverage</span>
                    <span className="oasys-brief-card-observation">{item.observation}</span>
                    <span className="oasys-five-brief-verified">Evidence verified <span aria-hidden="true">↗</span><span className="oasys-brief-card-open"> Open assessment</span></span>
                  </button>
                ))}
              </div>
              <div className="oasys-five-brief-actions-panel">
                <h3>Recommended next investigations</h3>
                <p>These actions are advisory. No remediation is authorized or executed from this Brief.</p>
                <ol>
                  {serviceEntries.filter(item => item.coverage !== 'observed').map(item => <li key={item.key}><strong>{item.title}:</strong> {item.action}</li>)}
                  <li>Evaluate the verified S3, IAM, and CloudTrail observations against the applicable control objectives before claiming control effectiveness.</li>
                </ol>
              </div>
              <label className="oasys-five-brief-acknowledge">
                <input type="checkbox" checked={reviewAcknowledged} onChange={event => setReviewAcknowledged(event.target.checked)} disabled={loading || saving} />
                <span>I reviewed the five service observations, coverage limitations, and the fact that compliance assurance has not been established.</span>
              </label>
            </>
          ) : (
            <div className="oasys-five-brief-blocked" role="status">
              <h3>Verified five-service Mission Brief unavailable</h3>
              <p>All five attempt-matched evidence receipts must be available before the Executive Mission Brief can be reviewed or completed.</p>
              <p>Refresh the Brief or contact the OASYS Team. No compliance conclusion is available from this view.</p>
            </div>
          )}
        </section>
        {activeService && fiveServiceReady ? (
          <div className="oasys-five-brief-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) closeService(); }}>
            <section className="oasys-five-brief-modal" role="dialog" aria-modal="true" aria-labelledby="oasys-five-brief-modal-title" ref={dialogRef} tabIndex={-1}>
              <div className="oasys-five-brief-modal-heading oasys-brief-presentation-r1" data-coverage={activeService.coverage}>
                <div><p className="mission-launch-eyebrow">AWS SERVICE ASSESSMENT</p><h3 id="oasys-five-brief-modal-title">{activeService.title}</h3><span className="oasys-brief-modal-status">{humanCoverage(activeService.coverage)} observation · Verified evidence</span></div>
                <button type="button" ref={closeRef} className="oasys-five-brief-modal-close" onClick={closeService} aria-label="Close service assessment">Close ×</button>
              </div>
              <dl className="oasys-five-brief-modal-content oasys-brief-presentation-r1">
                <div className="oasys-brief-modal-observation"><dt><span className="oasys-brief-section-index">01</span> What OASYS observed</dt><dd>{activeService.observation}</dd></div>
                <div className="oasys-brief-modal-impact"><dt><span className="oasys-brief-section-index">02</span> Why this matters</dt><dd>{activeService.significance}</dd></div>
                <div className="oasys-brief-modal-action"><dt><span className="oasys-brief-section-index">03</span> Recommended next investigation</dt><dd>{activeService.action}</dd><span className="oasys-brief-advisory">Advisory only · No AWS changes authorized</span></div>
                <div className="oasys-brief-modal-evidence"><dt><span className="oasys-brief-section-index">04</span> Evidence and provenance</dt><dd>Completed Mission Scan · Attempt-matched verification confirms receipt provenance, not detailed control effectiveness. <strong>{activeService.title}: verified.</strong></dd></div>
                <div className="oasys-brief-modal-limit"><dt>Scope and limitations</dt><dd>{activeService.limitation}{activeService.codes.length ? <ul>{activeService.codes.map((code, i) => <li key={`${code}-${i}`}>{humanCoverage(code)}</li>)}</ul> : null}</dd></div>
                {activeService.key === 's3' ? <div className="oasys-brief-modal-technical"><dt>S3 bounded inventory detail</dt><dd>Observed buckets: {brief.scan_evidence.observed_bucket_count ?? 'Unavailable'}. Review-required decisions: {brief.scan_evidence.review_required_count ?? 'Unavailable'}. Inventory coverage: {brief.scan_evidence.inventory_truncated ? 'truncated' : 'bounded; full coverage not asserted'}. Collection job: {brief.scan_evidence.collection_job_id || 'Unavailable'}.</dd></div> : null}
                <div className="oasys-brief-modal-boundary"><dt>Assurance boundary</dt><dd>Collection evidence verification is not a compliance or operating-effectiveness determination.</dd></div>
              </dl>
            </section>
          </div>
        ) : null}
        {!brief.scan_evidence?.available ? (
        <section
          className="mission-brief-prepared-section"
          aria-label="Prepared for Mission Execution"
        >
          <div className="mission-brief-prepared-header">
            <div>
              <p className="mission-launch-eyebrow">
                Prepared for Mission Execution
              </p>
              <h4>
                OASYS has translated the launch model into guided next steps
              </h4>
              <p>
                Review persisted findings, evidence, recommendations,
                validation results, and execution plans before taking action.
              </p>
            </div>
            <span className="mission-status-pill">
              Truth contract active
            </span>
          </div>

          <div className="mission-metric-grid mission-brief-prepared-grid">
            {executionPrepMetrics.map((metric) => (
              <MissionMetric key={metric.label} {...metric} />
            ))}
          </div>
        </section>

        ) : null}
        <div className="mission-step-actions mission-brief-actions">
          {readOnly ? (
            <p role="status">Mission Brief already completed. This is a read-only review; select the current step in Mission Progress to continue.</p>
          ) : (
            <button
              type="button"
              className="mission-primary-button"
              onClick={onContinue}
              disabled={loading || saving || Boolean(error) || !fiveServiceReady || !reviewAcknowledged || !brief.review_fingerprint}
            >
              {saving
                ? 'Confirming Executive Brief…'
                : 'Acknowledge & Continue to Operational Readiness'}
            </button>
          )}
        </div>
      </MissionCard>
    </div>
  );
}
